/**
 * usePhotoFromCommentGraphQL Hook
 *
 * Fetches the photo ID associated with a comment ID using GraphQL.
 * Used when URL contains ?comment=<id> without ?photo=<id>.
 *
 * Feature: 065-graphql-migration-cleanup
 */

import { gql, useQuery } from '@apollo/client';
import type { UsePhotoFromCommentReturn } from '../../types/critiqueUrlState';

const GET_CRITIQUE_PHOTO_ID = gql`
  query GetCritiquePhotoId($id: ID!) {
    critique(id: $id) {
      id
      photoId
    }
  }
`;

interface CritiquePhotoIdData {
  critique: {
    id: string;
    photoId: string;
  } | null;
}

/**
 * Fetch photo ID for a comment using GraphQL
 *
 * @param _collectionId - Global ID of the collection (unused but kept for API compatibility)
 * @param commentId - Global ID of the comment (null to skip fetch)
 * @returns Photo ID and loading state
 */
export function usePhotoFromCommentGraphQL(
  _collectionId: string,
  commentId: string | null
): UsePhotoFromCommentReturn {
  const { data, loading, error } = useQuery<CritiquePhotoIdData>(GET_CRITIQUE_PHOTO_ID, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: commentId || 'skip-placeholder' },
    skip: !commentId,
    fetchPolicy: 'cache-first',
  });

  return {
    photoId: data?.critique?.photoId ?? null,
    isLoading: loading,
    error: error ? error.message : null,
  };
}
